import { useState, useRef, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Flag, Globe2, ArrowUpRight, ArrowDown, HelpCircle } from "lucide-react";
import { SERVICE_TYPES } from "@/lib/data";
import ServiceCard from "@/components/ServiceCard";
import ServiceModal from "@/components/ServiceModal";
import Seo from "@/components/Seo";

const TYPE_ICONS = { us: Flag, eu: Globe2 };
const VALID_TRACKS = SERVICE_TYPES.map(t => t.id);

export default function Services() {
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTrack = searchParams.get("track");
    const [activeId, setActiveId] = useState(
        VALID_TRACKS.includes(initialTrack) ? initialTrack : null
    );
    const [modal, setModal] = useState(null);
    const gridRef = useRef(null);
    const active = SERVICE_TYPES.find(t => t.id === activeId);

    const handleSelect = (id) => {
        setActiveId(id);
        setSearchParams({ track: id }, { replace: true });
    };

    // Smooth scroll to the grid the first time a track is selected.
    useEffect(() => {
        if (!activeId || !gridRef.current) return;
        const t = setTimeout(() => {
            gridRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 80);
        return () => clearTimeout(t);
    }, [activeId]);

    return (
        <div data-testid="services-page">
            <Seo
                path="/services"
                title="Services — US Bookkeeping & EU Remote-Team Outsourcing"
                description="Two tracks built for global SMEs: US-aligned bookkeeping, taxation advisory and entity setup, or EU back-office talent on demand. Senior-led, secure, US-friendly."
                keywords="US bookkeeping services, taxation advisory services, business setup entity registration, remote team India, back office outsourcing Europe"
            />
            {/* Header */}
            <section className="container-page pt-16 md:pt-24 pb-10">
                <div className="flex items-center gap-3 mb-6">
                    <span className="ornament w-12" />
                    <p className="overline">Services</p>
                    <span aria-hidden="true" className="eyebrow-dot" />
                </div>
                <h1 className="display text-5xl md:text-6xl lg:text-7xl text-brand-ink max-w-4xl">
                    Two ways to work with <span className="italic text-brand-amber">Vittaniti</span><span className="brand-dot">.</span>
                </h1>
                <p className="mt-6 max-w-2xl text-brand-ink/70 leading-relaxed">
                    Pick the track that fits your business. We&apos;ll reveal the service lines built specifically for it.
                </p>
            </section>

            {/* Type selector */}
            <section className="container-page" aria-label="Choose a service track">
                <div role="tablist" data-testid="service-type-tabs" className="grid sm:grid-cols-2 gap-4 md:gap-6">
                    {SERVICE_TYPES.map((t, i) => {
                        const Icon = TYPE_ICONS[t.id];
                        const isActive = t.id === activeId;
                        return (
                            <button
                                key={t.id}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                aria-controls="service-grid"
                                onClick={() => handleSelect(t.id)}
                                data-testid={`service-type-${t.id}`}
                                className={`group relative text-left rounded-2xl p-7 md:p-8 border transition-all duration-300 animate-fade-up overflow-hidden ${
                                    isActive ? "text-white border-transparent" : "bg-white text-brand-ink border-brand-border hover:-translate-y-1"
                                }`}
                                style={{
                                    animationDelay: `${i * 90}ms`,
                                    ...(isActive
                                        ? { backgroundColor: "#142239", boxShadow: "0 18px 40px -18px rgba(20,34,57,0.45)" }
                                        : { boxShadow: "0 1px 0 rgba(20,34,57,0.04), 0 14px 30px -22px rgba(20,34,57,0.18)" }),
                                }}
                            >
                                <div className="flex items-start justify-between mb-5">
                                    <span className={`grid place-items-center w-12 h-12 rounded-xl ${
                                        isActive ? "bg-white/10 text-brand-amber" : "bg-brand-creamLight text-brand-ink"
                                    }`}>
                                        <Icon size={22} strokeWidth={1.6} />
                                    </span>
                                    <span className={`text-[10px] uppercase tracking-[0.22em] ${
                                        isActive ? "text-brand-amber" : "text-brand-muted"
                                    }`}>
                                        {t.kicker} · {t.services.length} services
                                    </span>
                                </div>
                                <h2 className={`font-serif text-2xl md:text-3xl leading-tight ${
                                    isActive ? "text-white" : "text-brand-ink"
                                }`}>
                                    {t.title}
                                </h2>
                                <p className="overline mt-2 text-brand-amber">{t.audience}</p>
                                <p className={`mt-4 text-sm leading-relaxed ${
                                    isActive ? "text-white/75" : "text-brand-ink/70"
                                }`}>
                                    {t.summary}
                                </p>
                                <span className={`mt-6 inline-flex items-center gap-1.5 text-xs font-semibold ${
                                    isActive ? "text-brand-amber" : "text-brand-ink"
                                }`}>
                                    {isActive ? (<>Showing services below <ArrowDown size={14} /></>) : (<>View services <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5" /></>)}
                                </span>

                                {isActive && (
                                    <span
                                        aria-hidden="true"
                                        className="absolute left-1/2 -bottom-3 -translate-x-1/2 w-6 h-6 rotate-45"
                                        style={{ backgroundColor: "#142239" }}
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* Reveal area */}
            <section className="section container-page" id="service-grid" aria-live="polite" data-testid="service-grid" ref={gridRef}>
                {!active && (
                    <div
                        data-testid="service-empty-state"
                        className="border border-dashed border-brand-border rounded-2xl p-12 md:p-16 text-center bg-white/40 animate-fade-in"
                    >
                        <p className="overline mb-3 text-brand-amber">Select a track above</p>
                        <h3 className="font-serif text-3xl md:text-4xl text-brand-ink leading-tight">
                            Choose how you want to work with us, <br className="hidden sm:block" />
                            and we&apos;ll reveal the matching services.
                        </h3>
                        <div className="mt-6 inline-flex items-center gap-2 text-brand-muted text-sm">
                            <ArrowDown size={16} className="animate-bounce text-brand-amber" />
                            <span>Click one of the cards above</span>
                        </div>
                    </div>
                )}

                {active && (
                    <div key={activeId}>
                        <div className="flex items-center gap-3 mb-8" data-testid="active-track-header">
                            <p className="overline">{active.audience}</p>
                            <span className="origin-left animate-rule-draw" style={{ animationDelay: "60ms" }}>
                                <span className="block rule max-w-md" />
                            </span>
                        </div>

                        <div
                            data-testid={`service-grid-${activeId}`}
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                        >
                            {active.services.map((s, i) => (
                                <div
                                    key={`${activeId}-${s.slug}`}
                                    className="animate-service-reveal"
                                    style={{ animationDelay: `${120 + i * 60}ms`, transformOrigin: "top center" }}
                                >
                                    <ServiceCard service={s} index={0} onSelect={setModal} region={activeId === "us" ? "US" : "EU"} animate={false} />
                                </div>
                            ))}

                            {activeId === "eu" && (
                                <Link
                                    to="/contact"
                                    data-testid="service-not-on-list"
                                    className="animate-service-reveal rounded-2xl p-7 md:p-8 flex flex-col h-full text-white relative overflow-hidden group"
                                    style={{
                                        animationDelay: `${120 + active.services.length * 60}ms`,
                                        transformOrigin: "top center",
                                        backgroundColor: "#142239",
                                        boxShadow: "0 14px 30px -18px rgba(20,34,57,0.40)",
                                    }}
                                >
                                    <span className="grid place-items-center w-12 h-12 rounded-xl bg-white/10 text-brand-amber mb-6">
                                        <HelpCircle size={22} strokeWidth={1.6} />
                                    </span>
                                    <p className="overline text-brand-amber mb-2">Not on the list?</p>
                                    <h3 className="font-serif text-2xl text-white leading-tight">
                                        Don&apos;t see your industry?
                                    </h3>
                                    <p className="text-sm text-white/70 leading-relaxed mt-4 flex-1">
                                        We work with businesses across every sector. Tell us what you do and
                                        we&apos;ll build a team to support your specific operations.
                                    </p>
                                    <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-amber">
                                        Book a free consultation
                                        <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                    </span>
                                </Link>
                            )}
                        </div>

                        {/* Free consultation CTA under each track */}
                        <div
                            data-testid={`track-cta-${activeId}`}
                            className="mt-12 rounded-2xl border border-brand-border bg-white px-7 py-7 md:px-10 md:py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
                            style={{ boxShadow: "0 14px 30px -22px rgba(20,34,57,0.18)" }}
                        >
                            <div className="max-w-xl">
                                <p className="overline text-brand-amber mb-2">Free consultation</p>
                                <h3 className="font-serif text-2xl md:text-3xl text-brand-ink leading-tight">
                                    Not sure which {activeId === "us" ? "service" : "remote-team setup"} fits? Let&apos;s talk it through.
                                </h3>
                                <p className="mt-2 text-sm text-brand-muted">
                                    A 30-minute call with a Vittaniti partner — scoped to your business, no obligation.
                                </p>
                            </div>
                            <Link to="/contact" className="btn-primary shrink-0" data-testid={`track-cta-link-${activeId}`}>
                                Book a free consultation <ArrowUpRight size={16} />
                            </Link>
                        </div>
                    </div>
                )}
            </section>

            <ServiceModal service={modal} open={!!modal} onOpenChange={(v) => !v && setModal(null)} />
        </div>
    );
}
